(function(w) {
  'use strict';

  Cnvs.Utils = {

    el: {
      pfp: document.querySelector(".pfp"),
      acrylicCB: document.querySelector("#vpacr"),
      PUacrylicCB: document.querySelector("#epuacr")
    },
    
    // ================================================================
    // PERFORMANCE UTILITIES
    // ================================================================
    
    // Debounce function calls
    debounce(func, wait, immediate = false) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          timeout = null;
          if (!immediate) func(...args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func(...args);
      };
    },

    // Throttle function calls
    throttle(func, limit) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    // ================================================================
    // STRING UTILITIES
    // ================================================================
    
    // Generate random ID
    makeId(length = 8) {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      return Array.from({ length }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
    },

    // Capitalize first letter
    capitalize(str) {
      if (!str || typeof str !== 'string') return str;
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    // Truncate string with ellipsis
    truncate(str, length = 50, suffix = '...') {
      if (!str || str.length <= length) return str;
      return str.substring(0, length - suffix.length) + suffix;
    },

    // ================================================================
    // DATA CONVERSION UTILITIES (CONSOLIDATED)
    // ================================================================
    
    // Convert base64 to blob (consolidated implementation)
    base64ToBlob(input, mimeType = null) {
      try {
        let base64Data, mime;
        
        // Handle data URI format
        if (typeof input === 'string' && input.includes(',')) {
          const [meta, data] = input.split(',');
          base64Data = data;
          mime = mimeType || meta.match(/data:(.*?);/)?.[1] || 'application/octet-stream';
        } 
        // Handle raw base64
        else if (typeof input === 'string') {
          base64Data = input;
          mime = mimeType || 'application/octet-stream';
        } 
        else {
          throw new Error('Invalid input: expected base64 string or data URI');
        }
        
        const byteString = atob(base64Data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);
        
        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i);
        }
        
        return new Blob([arrayBuffer], { type: mime });
      } catch (error) {
        console.error('Failed to convert base64 to blob:', error);
        return null;
      }
    },

    // ================================================================
    // ARRAY UTILITIES
    // ================================================================
    
    // Remove duplicates from array
    unique(array) {
      return [...new Set(array)];
    },

    // Group array by key function
    groupBy(array, keyFn) {
      return array.reduce((groups, item) => {
        const key = typeof keyFn === 'function' ? keyFn(item) : item[keyFn];
        groups[key] = groups[key] || [];
        groups[key].push(item);
        return groups;
      }, {});
    },

    // ================================================================
    // OBJECT UTILITIES
    // ================================================================
    
    // Deep clone object
    clone(obj) {
      if (obj === null || typeof obj !== 'object') return obj;
      if (obj instanceof Date) return new Date(obj);
      if (obj instanceof Array) return obj.map(item => this.clone(item));
      if (typeof obj === 'object') {
        const cloned = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            cloned[key] = this.clone(obj[key]);
          }
        }
        return cloned;
      }
    },

    // Merge objects deeply
    merge(target, ...sources) {
      if (!sources.length) return target;
      const source = sources.shift();

      if (this.isObject(target) && this.isObject(source)) {
        for (const key in source) {
          if (this.isObject(source[key])) {
            if (!target[key]) Object.assign(target, { [key]: {} });
            this.merge(target[key], source[key]);
          } else {
            Object.assign(target, { [key]: source[key] });
          }
        }
      }

      return this.merge(target, ...sources);
    },

    // Check if value is object
    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item);
    },

    // Get nested object property safely
    get(obj, path, defaultValue = null) {
      const keys = path.split('.');
      let current = obj;
      for (const key of keys) {
        if (current == null || typeof current !== 'object') {
          return defaultValue;
        }
        current = current[key];
      }
      return current !== undefined ? current : defaultValue;
    },

    // Set nested object property safely
    set(obj, path, value) {
      const keys = path.split('.');
      const lastKey = keys.pop();
      let current = obj;
      
      for (const key of keys) {
        if (current[key] == null || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
      
      current[lastKey] = value;
    },

    // ================================================================
    // DOM UTILITIES (using existing S$Q, S$$, S$G)
    // ================================================================
    
    // Safe element access with error handling
    safeQuery(selector, fallback = null) {
      try {
        return document.querySelector(selector) || fallback;
      } catch (error) {
        console.warn(`Failed to query: ${selector}`, error);
        return fallback;
      }
    },

    safeQueryAll(selector, fallback = []) {
      try {
        return [...document.querySelectorAll(selector)];
      } catch (error) {
        console.warn(`Failed to query all: ${selector}`, error);
        return fallback;
      }
    },

    // Check if element exists
    exists(selector) {
      try {
        return Boolean(document.querySelector(selector));
      } catch (e) {
        return false;
      }
    },

    // ================================================================
    // FILE UTILITIES
    // ================================================================
    
    // Format file size
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return this.round(bytes / Math.pow(k, i), 2) + ' ' + sizes[i];
    },

    // Get file extension
    getFileExtension(filename) {
      return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
    },

    // ================================================================
    // MODULE UTILITIES
    // ================================================================
    
    // Log module info
    logModuleInfo() {
      console.groupCollapsed('📦 Cnvs.Utils module loaded - Available utilities:');
      console.log(Object.keys(this).filter(key => typeof this[key] === 'function'));
      console.groupEnd();
    },

    // ================================================================
    // BLOB CONVERSION UTILITIES (moved to main function above)
    // ================================================================

    // Add crop parameters to Filestack URL
    addCropToFilestackUrl(url, cor, bpx, edge, bw) {
      let bpxr = Math.round(bpx);
      let coordinates = cor.split(",");
      let parts = url.split("/");
      let handle = parts.pop();
      let baseUrl = parts.join("/");
      let border = bpxr > 0 && edge != "Wrapped" ? `border=width:${bpxr},color:${edge}/` : '';
      let filter = bw ? `monochrome/` : '';
      let newUrl = `${baseUrl}/rotate=deg:exif/crop=d:[${coordinates[0]},${coordinates[1]},${coordinates[2]},${coordinates[3]}]/${filter}${border}${handle}`;
      return newUrl;
    },

    // Calculate new left position for resizing
    getNewLeft(oldLeft, oldBoxWidth, newBoxWidth, oldScale, newScale) {
      return (
        (newBoxWidth / 2) - (((oldBoxWidth / 2) - oldLeft) / oldScale) * newScale
      );
    },

    // Calculate distance between two touch points
    getTouchDistance(touches) {
      const [touch1, touch2] = touches;
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    // Get scale value from CSS transform
    getScale(element) {
      var computedStyle = w.getComputedStyle(element).transform;
      var matrix = computedStyle != "none" ? computedStyle.match(/^matrix\((.+)\)$/)[1].split(', ') : [0];
      return parseFloat(matrix[0]);
    },

    // Calculate total price from all frame elements
    calculateTotalPrice(selector = '.finfo_price') {
      return [...document.querySelectorAll(selector)].reduce((sum, el) => {
        return sum + (parseFloat(el.innerText.replace(/[^\d.-]/g, '')) || 0);
      }, 0);
    },

    getTransformOrigin(transformOrigin) {
      const values = transformOrigin
        .replace(/[^\d.\s]/g, '') 
        .trim()
        .split(/\s+/)
        .map(Number); 

      return {
        x: values[0] || 0,
        y: values[1] || 0
      };
    },

    // ================================================================
    // BLOB CONVERSION UTILITIES - TRUE EXTRACTION FROM MAIN FILE
    // ================================================================

    // Convert image element to blob using canvas
    getBlobFromImageElement(imgElement) {
      return new Promise((resolve, reject) => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = imgElement.naturalWidth;
          canvas.height = imgElement.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(imgElement, 0, 0);
          canvas.toBlob(blob => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas toBlob failed"));
            }
          }, 'image/png');
        } catch (err) {
          reject(err);
        }
      });
    },

    // Smart blob fetching with canvas fallback
    async getBlobSmart(imgElement) {
      if (!imgElement || !imgElement.src) return null;

      if (imgElement.src.startsWith('blob:')) {
        try {
          return await fetch(imgElement.src).then(res => res.blob());
        } catch (e) {
          console.warn("fetch() failed, using canvas fallback", e);
          return this.getBlobFromImageElement(imgElement);
        }
      }
      return null;
    },

    // ================================================================
    // VALIDATION UTILITIES
    // ================================================================
    
    // Validate email format
    isValidEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },

    // Validate URL format
    isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    },

    // Validate image file
    isValidImageFile(file) {
      if (!file) return false;
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return validTypes.includes(file.type);
    },

    // Format file size for display
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // Get file extension
    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop().toLowerCase();
    },

    // ================================================================
    // LOGGING UTILITIES
    // ================================================================
    
    // Enhanced logging
    logInfo(...args) {cLog('📘 Cnvs.Utils:', ...args);},
    logWarn(...args) {cWarn('⚠️ Cnvs.Utils:', ...args);},
    logError(...args) {cError('❌ Cnvs.Utils:', ...args);},

    // ================================================================
    // VALIDATION UTILITIES - TRUE EXTRACTION FROM MAIN FILE (PHASE 3)
    // ================================================================

    // Validate frame object and return error array
    validateFrame(frame) {
      const errors = [];
      
      if (!frame.style) errors.push('Frame style is required');
      if (!frame.size) errors.push('Frame size is required');
      if (!frame.image.previewURL && !frame.image.filestackUrl) {
        errors.push('Frame must have an image');
      }
      
      return errors;
    },

    // Check if all frames have same size (Frame Size Consistency)
    checkFrameSizeConsistency() {
      const frames = document.querySelectorAll('.framefh');
      if (!frames.length) return true;

      const firstWidth = frames[0].getAttribute('data-width-in-inches');
      const firstHeight = frames[0].getAttribute('data-height-in-inches');

      const allSame = Array.from(frames).every(frame => 
          frame.getAttribute('data-width-in-inches') === firstWidth &&
          frame.getAttribute('data-height-in-inches') === firstHeight
      );

      return allSame;
    },

    // Check if all frames have same property value (Frame Consistency)
    checkFrameConsistency(property) {
      const frames = document.querySelectorAll('.framefh');
      if (!frames.length) return true;
      
      const firstValue = frames[0].getAttribute('data-frame-' + property);
      const allSame = [...frames].every(frame => 
        frame.getAttribute('data-frame-' + property) === firstValue
      );
      
      return allSame;
    },

    // ================================================================
    // VIEWPORT MANAGEMENT UTILITIES - TRUE EXTRACTION FROM MAIN FILE (PHASE 5)
    // ================================================================

    // Navigate swiper to slide with specific data attribute
    jump2Slide(dataSize, theSwiper) {
      if (!theSwiper || !theSwiper.slides) return false;
      
      let targetIndex = null;
      theSwiper.slides.each(function (index, slide) {
        if (slide.getAttribute('data-size') === dataSize) {
          targetIndex = index;
          return false;
        }
      });

      if (targetIndex !== null) {
        theSwiper.slideTo(targetIndex);
        return true;
      }
      return false;
    },

    // Update safe area insets for mobile viewport handling
    updateSafeAreaInsets() {
      try {
        const computed = getComputedStyle(document.documentElement);

        const inset = side => 
          parseFloat(computed.getPropertyValue(`--safe-area-inset-${side}`)) ||
          (w.visualViewport ? Math.max(0, {
            top: visualViewport.offsetTop,
            bottom: w.innerHeight - visualViewport.height - visualViewport.offsetTop,
            left: visualViewport.offsetLeft,
            right: w.innerWidth - visualViewport.width - visualViewport.offsetLeft
          }[side]) : 0);

        ['top', 'right', 'bottom', 'left'].forEach(side =>
          document.documentElement.style.setProperty(`--safe-area-inset-${side}`, `${inset(side)}px`)
        );
        
        return true;
      } catch (error) {
        console.error('Error updating safe area insets:', error);
        return false;
      }
    },

    findPByKey(substringo, substringt) {
      try {
        // Basic product lookup logic
        if (typeof prdctmap !== 'undefined' && prdctmap) {
          const keys = Object.keys(prdctmap);
          const matchingKey = keys.find(key => key.includes(substringo));
          
          if (matchingKey && prdctmap[matchingKey]) {
            return {
              id: prdctmap[matchingKey].id,
              sku: prdctmap[matchingKey].sku,
              price: prdctmap[matchingKey].price || 55,
              title: matchingKey,
              available: prdctmap[matchingKey].available || true,
              image: prdctmap[matchingKey].image || ''
            };
          }
        }
        
        // Always return a valid object (never null)
        console.warn(`⚠️ Product lookup fallback for: ${substringo}`);
        return {
          id: `fallback-${Date.now()}`,
          price: 55, // Default price
          title: substringo || 'Unknown Product',
          available: true
        };
      } catch (error) {
        console.error('❌ Error in findPByKey:', error);
        // Return safe fallback
        return {
          id: `error-${Date.now()}`,
          price: 55,
          title: 'Error Product',
          available: true
        };
      }
    },

    findVariantByKeySubstring(substringo, substringt) {
      try {
        // Check if we have acrylic variant data
        if (typeof acrVID !== 'undefined' && acrVID) {
          const keys = Object.keys(acrVID);
          const matchingKey = keys.find(key => key.includes(substringo));
          
          if (matchingKey && acrVID[matchingKey]) {
            return {
              id: acrVID[matchingKey].id,
              price: acrVID[matchingKey].price || 8,
              title: matchingKey,
              available: acrVID[matchingKey].available || true,
              image: acrVID[matchingKey].image || ''
            };
          }
        }
        
        // Always return a valid object (never null)
        console.warn(`⚠️ Variant lookup fallback for: ${substringo}`);
        // return {
        //   id: `fallback-variant-${Date.now()}`,
        //   price: 8, // Default variant price
        //   title: substringo || 'Unknown Variant'
        // };
        return null;
      } catch (error) {
        console.error('❌ Error in findVariantByKeySubstring:', error);
        // Return safe fallback
        return {
          id: `error-variant-${Date.now()}`,
          price: 8,
          title: 'Error Variant'
        };
      }
    },

    async upload2filestack(b64, apikey = 'Av52UkFmsQ1aDQgxjEjyrz') {
      try {
        if (!b64 || typeof b64 !== 'string') {
          throw new Error('Invalid base64 data provided');
        }
        
        if (typeof filestack === 'undefined') {
          throw new Error('Filestack library not available');
        }
        
        const client = filestack.init(apikey);
        
        // Extract base64 data (remove data:image/... prefix if present)
        const base64Data = b64.includes(',') ? b64.split(',')[1] : b64;
        
        // Convert to blob using existing utility
        const blob = this.base64ToBlob(base64Data, 'image/png');
        
        // Create file from blob
        const file = new File([blob], 'uploaded-image.png', { type: 'image/png' });
        
        cLog('☁️ Uploading to Filestack...');
        const uploadResult = await client.upload(file);
        
        cLog('✅ File uploaded to Filestack:', uploadResult.url);
        return uploadResult.url;
        
      } catch (error) {
        console.error('❌ Error uploading to Filestack:', error);
        throw error;
      }
    },
    
    async uploadAllFramesToFilestack(globalRefs) {
      try {
        if (!globalRefs) {
          console.error('❌ uploadAllFramesToFilestack: globalRefs object required');
          return 0;
        }
        
        // Get frames that need uploading (no data-image attribute or empty)
        const frames = Array.from(document.querySelectorAll('.dp_wrapper .framefh'))
          .filter(frame => !frame.dataset.image || frame.dataset.image.trim() === "");
        
        const allframes = Array.from(document.querySelectorAll('.dp_wrapper .framefh'));
        const total = frames.length;
        
        cLog(`📤 Uploading ${total} frames to Filestack`);
        
        if (total === 0 && allframes.length > 0) {
          globalRefs.lefttup = 0;
          return 0;
        } else if (total === 0 && allframes.length === 0) {
          return 0;
        }
        
        let uploadedCount = 0;
        
        for (let i = 0; i < total; i++) {
          try {
            // Upload single frame using existing function
            if (typeof Cnvs.Images.uploadSingleFrame === 'function') {
              await Cnvs.Images.uploadSingleFrame(frames[i]);
              uploadedCount++;
              cLog(`✅ Uploaded frame ${i + 1}/${total}`);
            }
            
            // Update global progress tracking
            globalRefs.lefttup = total - (i + 1);
            
            // Save progress to database
            Cnvs?.State?.scheduleSave();
            
          } catch (frameError) {
            console.error(`❌ Error uploading frame ${i + 1}:`, frameError);
          }
        }
        
        cLog(`✅ Upload complete: ${uploadedCount}/${total} frames uploaded`);
        return uploadedCount;
        
      } catch (error) {
        console.error('❌ Error in uploadAllFramesToFilestack:', error);
        return 0;
      }
    },
    
    generateFrameForestFilename(extension = '') {
      const newDate = new Date();
      const newMonth = newDate.getMonth() + 1;
      const newYear = newDate.getFullYear();
      const timestamp = Date.now().toString(36);
      const randomId = this.makeId(8);
      
      return `f_f-${newMonth}_${newYear}-${timestamp}${randomId}${extension}`;
    },
    
    async convertImageElementToBlob(imgElement) {
      try {
        if (!imgElement || !imgElement.src) {
          throw new Error('Invalid image element provided');
        }
        
        const src = imgElement.src;
        
        if (src.startsWith('blob:')) {
          const response = await fetch(src);
          return await response.blob();
        } else if (src.startsWith('data:image')) {
          return this.base64ToBlob(src);
        } else {
          const response = await fetch(src);
          return await response.blob();
        }
        
      } catch (error) {
        console.error('❌ Error converting image to blob:', error);
        throw error;
      }
    },

    generateS3Url() {
     const baseUrl = "https://ffpf.s3.us-west-1.amazonaws.com/tp/";

     const now = new Date();

     const yy = String(now.getFullYear()).slice(2); 
     const mm = String(now.getMonth() + 1).padStart(2, '0'); 
     const dd = String(now.getDate()).padStart(2, '0');
     const hh = String(now.getHours()).padStart(2, '0');
     const min = String(now.getMinutes()).padStart(2, '0'); 

     const random = this.generateRandomAlphaNumeric(8); 

     const yymm = `${yy}${mm}`;
     const timestamp = `${dd}_${hh}${min}`;
     const fileName = `${timestamp}_${random}`;

     return `${baseUrl}${yymm}/${fileName}`;
    },

    generateRandomAlphaNumeric(length) {
     const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
     let result = '';
     for (let i = 0; i < length; i++) {
     result += chars.charAt(Math.floor(Math.random() * chars.length));
     }
     return result;
    },


    sanitizeAppStateForStorage(state) {
      let clone;
      try {
        clone = typeof structuredClone === "function"
          ? structuredClone(state)
          : JSON.parse(JSON.stringify(state));
      } catch (e) {
        console.error("Error cloning app state:", e);
        return state;
      }

      clone.frames?.forEach(frame => {
        if (frame?.image?.file !== undefined && frame?.image?.filestackUrl) {
          delete frame.image.file;
        }
      });

      return clone;
    },
  };

  // Mark module as loaded
  if (Cnvs.moduleStatus) {
    Cnvs.moduleStatus.utils = true;
    Cnvs.checkModules?.();
  }

  // Auto-log on load (can be disabled)
  if (typeof w !== 'undefined' && !Cnvs._silentLoad) Cnvs.Utils.logModuleInfo();

})(window); 